<template>
  <component :is="componentMap[page?.section]" v-if="page" :page="page" :breadcrumbs="breadcrumbs" />
</template>

<script setup lang="ts">
import { getIndirectClientHeaders, seoHead } from '~/assets/seo'
import { getBreadcrumbs, getHomeUrl, getSiteConfig } from '~/composables/craft'
import { setCacheControlHeader, setPageCacheControlHeader } from '~/composables/util'
import { useSeoRedirect } from '~/composables/seo-redirect'
import { useNavigationStore } from '~/store/navigation'
import { useGlobalStore } from '~/store/global'
import { usePageStore } from '~/store/page'
import { useVariantHomeDetailStore } from '~/store/variant-home-detail'
import { useAnimalSuccessOverviewStore } from '~/store/animal-success-overview'
import { useArticleOverviewStore } from '~/store/article-overview'
import { useDepartmentOverviewStore } from '~/store/department-overview'
import { useJobOfferOverviewStore } from '~/store/job-offer-overview'
import { usePageOverviewStore } from '~/store/page-overview'

/**
 * Routes will be split on the '~~' characters {@see nuxt/app/router.options.ts}
 * Using a hack here `[pathMatch0]/[pathMatch]*` to capture paths of 1..n segments,
 * because `[pathMatch]` only captures 1 segment and `[pathMatch]*` captures 0..n segments
 * (and this will conflict with the route of {@see nuxt/pages/landing-detail.vue}).
 */
false && /*#__PURE__*/ defineI18nRoute({
  paths: {
    nl: '/www/[departmentTypeSlug]/[siteSlug]/[pathMatch0]/[pathMatch]*~~/www/[pathMatch]*~~/ikzoekbaas/[pathMatch]*~~/mijndieriszoek/[pathMatch]*~~/inspectiedienst/[pathMatch]*',
  },
})

const route = useRoute()
const navigationStore = useNavigationStore()
const globalStore = useGlobalStore()
const pageStore = usePageStore()
const variantHomeDetailStore = useVariantHomeDetailStore()
const animalSuccessOverviewStore = useAnimalSuccessOverviewStore()
const articleOverviewStore = useArticleOverviewStore()
const departmentOverviewStore = useDepartmentOverviewStore()
const jobOfferOverviewStore = useJobOfferOverviewStore()
const pageOverviewStore = usePageOverviewStore()
const config = useRuntimeConfig()
const seoRedirect = useSeoRedirect()

const { site, siteGroup } = getSiteConfig(route)

let uri = [route.params.pathMatch0 || undefined, Array.isArray(route.params.pathMatch) ? route.params.pathMatch.join('/') : route.params.pathMatch]
  .filter((segment) => segment)
  .join('/')
/**
 * [DB-748] Vue-router swallows a trailing slash when matching the route, so re-attach it from the
 * requested path. This way the URI reaches the CMS as-is and the CMS decides to redirect a URL
 * with a trailing slash to the canonical URL without it.
 */
if (uri && route.path.endsWith('/')) {
  uri += '/'
}
let seoGlobal
let page
try {
  ;[, , seoGlobal, page] = await Promise.all([
    navigationStore.fetch(site),
    globalStore.fetch({ site, push: true }),
    globalStore.fetchSeo(),
    pageStore.fetchUri({ site, uri, push: true, options: { headers: getIndirectClientHeaders(), notFoundStrategy: config.public.environment !== 'dev' ? 'ignore' : undefined } }),
  ])
} catch (error) {
  console.error('Error on "pages/[...slug].vue"', error)
  throw error
}

if (!page) {
  setCacheControlHeader(600, true)
  let redirect
  try {
    redirect = await seoRedirect(site, route.fullPath)
  } catch (error) {
    console.error('Error on "pages/[...slug].vue"', error)
    throw error
  }
  if (redirect === false) {
    throw createError({ statusCode: 404, message: 'Page not found', fatal: true })
  }
}

// Some pages require an `await` for SEO purposes
switch (page?.section) {
  case 'animalHome': {
    variantHomeDetailStore.initialize({ site })
    await variantHomeDetailStore.fetch()
    break
  }
  case 'animalSuccessOverview': {
    animalSuccessOverviewStore.initialize(route.query)
    await animalSuccessOverviewStore.fetch()
    break
  }
  case 'articleOverview': {
    articleOverviewStore.initialize({ ...route.query, site, siteGroup })
    await Promise.all([articleOverviewStore.fetchAllFacets(), articleOverviewStore.fetch()])
    break
  }
  case 'departmentOverview': {
    departmentOverviewStore.initialize({ ...route.query, departmentType: page.departmentType.title, departmentVisibility: site })
    await departmentOverviewStore.fetch()
    break
  }
  case 'jobOfferOverview': {
    jobOfferOverviewStore.initialize({ ...route.query, type: page.jobOfferType })
    await Promise.all([jobOfferOverviewStore.fetchFacets(), jobOfferOverviewStore.fetch()])
    break
  }
  case 'pageOverview': {
    pageOverviewStore.initialize(route.query)
    await pageOverviewStore.fetch()
    break
  }
}

const genericComponentMap = {
  animalHome: resolveComponent('TemplateVariantHomeDetail'),
  animalOverview: resolveComponent('TemplateAnimalOverview'),
  animalSuccess: resolveComponent('TemplateAnimalSuccessDetail'),
  animalSuccessOverview: resolveComponent('TemplateAnimalSuccessOverview'),
  article: resolveComponent('TemplateArticleDetail'),
  articleOverview: resolveComponent('TemplateArticleOverview'),
  collectionPage: resolveComponent('TemplateCollectionPage'),
  departmentOverview: resolveComponent('TemplateDepartmentOverview'),
  jobOffer: resolveComponent('TemplateJobOfferDetail'),
  jobOfferOverview: resolveComponent('TemplateJobOfferOverview'),
  landing: resolveComponent('TemplateLandingDetail'),
  overview: resolveComponent('TemplateOverview'),
  page: resolveComponent('TemplatePageDetail'),
  pageOverview: resolveComponent('TemplatePageOverview'),
  special: resolveComponent('TemplateSpecial'),
}
const siteComponentMaps = {
  inspectiedienst: {
    article: resolveComponent('TemplateLidArticleDetail'),
    articleOverview: resolveComponent('TemplateLidArticleOverview'),
    landing: resolveComponent('TemplateLidLandingDetail'),
    overview: resolveComponent('TemplateLidOverview'),
    page: resolveComponent('TemplateLidPageDetail'),
  },
}
const componentMap = { ...genericComponentMap, ...siteComponentMaps[site] }

const breadcrumbPrefixes = []
switch (page?.section) {
  case 'article':
    breadcrumbPrefixes.push({ label: 'Actueel', url: getHomeUrl(route, '/actueel').url })
    break
}

const breadcrumbs = getBreadcrumbs(route, page, breadcrumbPrefixes)

if (page) {
  setPageCacheControlHeader(page)

  useHead(() => {
    const options = {}
    switch (page.section) {
      case 'animalOverview':
      case 'articleOverview':
      case 'departmentOverview':
      case 'jobOfferOverview':
      case 'pageOverview':
        options.canonicalQuery = Object.fromEntries(['page'].map((query) => [query, route.query[query]]))
        break
    }

    return seoHead(page, seoGlobal, options)
  })
}
</script>
